<template>
  <section class="tonal-audiometry-form pb-3">

    <div class="d-flex justify-content-between flex-wrap align-items-center">
      <h6 class="text-evollu-gray-light is-bold py-3 mb-0">
          {{$t('clinical.people.anamnesis.tonal_audiometry_title')}}
      </h6>

      <div class="d-flex align-items-center justify-content-end">

        <div v-if="hasUnsavedChanges">
          <small class="text-orange is-bold">
            {{$t('clinical.people.anamnesis.unsaved')}}
          </small>
        </div>

        <div v-else-if="value && value.updated_at !== null && value.updated_at !== undefined">
          <small>
            {{$t('clinical.people.anamnesis.updated_at')}}
          </small>
          <small class="is-bold">
            {{$t('clinical.people.anamnesis.updated_by', {date: getExpressiveDate(value.updated_at), name: value.updated_by ? value.updated_by.name : ''})}}
          </small>
        </div>

        <i
          v-if="collapsable"
          class="clickable p-3"
          :class="{'fa fa-chevron-up': !isCollapsed, 'fa fa-chevron-down': isCollapsed}"
          @click="isCollapsed = !isCollapsed"
        >
        </i>
      </div>

    </div>

    <div v-if="!isCollapsed" class="mt-3">

      <slot name="beforeForm"></slot>

      <form class="evollu-form" v-if="value">

        <div class="table-wrapper mt-3 mb-5">
          <table class="table table-responsive-xl table-borderless metrics-table">
            <thead>
              <tr>
                <th class="field">
                </th>
                <th scope="col" v-for="(column, index) in metricsConfigs.tableColumns" :key="index" class="frequency">
                  {{$t(column.label)}}
                </th>
                <th class="masking">
                  {{$t('clinical.people.anamnesis.masking')}}
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(metric, i) in metricsConfigs.metrics" :key="i">
                <td class="field">
                  <span class="is-bold">{{$t(metric.label)}}</span>
                </td>

                <td v-for="(column, j) in metricsConfigs.tableColumns" :key="j" class="frequency">
                  <va-input
                    v-if="metric.frequencies.includes(column.value)"
                    :id="`${metric.field}-${column.value}`"
                    :value="getMetricValue(metric.field, metric.ear, column.value)"
                    @input="v => updateMetricValue(metric.field, metric.ear, column.value, v)"
                    :maxlength="7"
                    :disabled="!editing"
                    :error="formErrors.has(`metric_${metric.field}_${metric.ear}_${column.value}_value`)"
                  />
                </td>

                <td class="masking">

                  <multiselect
                      v-if="metric.masking_field !== null"
                      :value="formOptions.booleanOptions.find(o => o.value === value[metric.masking_field])"
                      :options="formOptions.booleanOptions"
                      @input="v => updateValue(metric.masking_field, v ? v.value : null)"
                      track-by="id"
                      :customLabel="option => $t(option.name)"
                      select-label=""
                      selected-label=""
                      deselect-label=""
                      placeholder="---"
                      :allow-empty="true"
                      :multiple="false"
                      open-direction="bottom"
                      :disabled="!editing"
                      >

                      <template slot="clear" v-if="editing">
                        <span class="multiselect__clear" v-if="value[metric.masking_field] !== null && value[metric.masking_field] !== undefined" @mousedown.prevent.stop="updateValue(metric.masking_field, null)"></span>
                      </template>
                    </multiselect>
                </td>

              </tr>
            </tbody>
          </table>

          <div v-if="cumulativeMetricErrors.length > 0">
            <div v-for="(item, index) in cumulativeMetricErrors" :key="index">
              <small class="text-danger">{{$t(item)}}</small>
            </div>
          </div>

        </div>

        <!-- PTA Right ear -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.right_ear_avg_areal_hearing_loss')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="right-ear-areal-hearing-loss"
              type="number"
              step="0.1"
              max="150"
              min="0"
              :maxlength="7"
              :placeholder="$t('clinical.people.anamnesis.db_hl')"
              :value="value.right_ear_avg_areal_hearing_loss"
              @input="v => updateValue('right_ear_avg_areal_hearing_loss', v)"
              :error="formErrors.has('right_ear_avg_areal_hearing_loss')"
              :error-messages="[$t(formErrors.get('right_ear_avg_areal_hearing_loss'))]"
              :disabled="!editing || autoCalcPTARight"
            />
          </div>
        </div>

        <!-- PTA Left ear -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.left_ear_avg_areal_hearing_loss')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="left-ear-areal-hearing-loss"
              type="number"
              step="0.1"
              max="150"
              min="0"
              :maxlength="7"
              :placeholder="$t('clinical.people.anamnesis.db_hl')"
              :value="value.left_ear_avg_areal_hearing_loss"
              @input="v => updateValue('left_ear_avg_areal_hearing_loss', v)"
              :error="formErrors.has('left_ear_avg_areal_hearing_loss')"
              :error-messages="[$t(formErrors.get('left_ear_avg_areal_hearing_loss'))]"
              :disabled="!editing || autoCalcPTALeft"
            />
          </div>
        </div>

        <!-- Right Ear Hearing Loss Type -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.right_ear_hearing_loss_type')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <multiselect
              :value="formOptions.tonalAudiometryHearingLossTypes.filter(o => value.right_ear_hearing_loss_type === o.id)"
              :options="formOptions.tonalAudiometryHearingLossTypes"
              @input="v => updateValue('right_ear_hearing_loss_type', v ? v.id : null)"
              track-by="id"
              :customLabel="option => $t(option.name)"
              select-label=""
              selected-label=""
              deselect-label=""
              placeholder="---"
              :allow-empty="true"
              :multiple="false"
              open-direction="bottom"
              :disabled="!editing"
              >
                <span slot="noResult">{{$t('patients.noResults')}}</span>
                <template slot="clear" v-if="editing">
                  <span class="multiselect__clear" v-if="value.right_ear_hearing_loss_type !== null && value.right_ear_hearing_loss_type !== undefined" @mousedown.prevent.stop="updateValue('right_ear_hearing_loss_type', null)"></span>
                </template>
              </multiselect>
            </div>

          <div class="text-danger col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-5 offset-md-4" v-if="formErrors.has('right_ear_hearing_loss_type')">
            <small>
              {{$t(formErrors.get('right_ear_hearing_loss_type'))}}
            </small>
          </div>
        </div>

        <!-- Left Ear Hearing Loss Type -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.left_ear_hearing_loss_type')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <multiselect
              :value="formOptions.tonalAudiometryHearingLossTypes.filter(o => value.left_ear_hearing_loss_type === o.id)"
              :options="formOptions.tonalAudiometryHearingLossTypes"
              @input="v => updateValue('left_ear_hearing_loss_type', v ? v.id : null)"
              track-by="id"
              :customLabel="option => $t(option.name)"
              select-label=""
              selected-label=""
              deselect-label=""
              placeholder="---"
              :allow-empty="true"
              :multiple="false"
              open-direction="bottom"
              :disabled="!editing"
              >
                <span slot="noResult">{{$t('patients.noResults')}}</span>
                <template slot="clear" v-if="editing">
                  <span class="multiselect__clear" v-if="value.left_ear_hearing_loss_type !== null && value.left_ear_hearing_loss_type !== undefined" @mousedown.prevent.stop="updateValue('left_ear_hearing_loss_type', null)"></span>
                </template>
              </multiselect>
            </div>

          <div class="text-danger col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-5 offset-md-4" v-if="formErrors.has('left_ear_hearing_loss_type')">
            <small>
              {{$t(formErrors.get('left_ear_hearing_loss_type'))}}
            </small>
          </div>
        </div>

        <!-- Bone Hearing Loss Right ear -->
        <!--
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.right_ear_avg_bone_hearing_loss')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="right-ear-bone-hearing-loss"
              type="number"
              step="0.1"
              max="150"
              min="0"
              :placeholder="$t('clinical.people.anamnesis.db_hl')"
              :value="value.right_ear_avg_bone_hearing_loss"
              @input="v => updateValue('right_ear_avg_bone_hearing_loss', v)"
              :error="formErrors.has('right_ear_avg_bone_hearing_loss')"
              :error-messages="[$t(formErrors.get('right_ear_avg_bone_hearing_loss'))]"
              :disabled="!editing"
            />
          </div>
        </div>
        -->

        <!-- Bone Hearing Loss Left ear -->
        <!--
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.left_ear_avg_bone_hearing_loss')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="left-ear-bone-hearing-loss"
              type="number"
              step="0.1"
              max="150"
              min="0"
              :placeholder="$t('clinical.people.anamnesis.db_hl')"
              :value="value.left_ear_avg_bone_hearing_loss"
              @input="v => updateValue('left_ear_avg_bone_hearing_loss', v)"
              :error="formErrors.has('left_ear_avg_bone_hearing_loss')"
              :error-messages="[$t(formErrors.get('left_ear_avg_bone_hearing_loss'))]"
              :disabled="!editing"
            />
          </div>
        </div>
        -->

        <!-- ULL -->
        <!--
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.ull')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="ull"
              type="number"
              step="0.1"
              max="150"
              min="0"
              :placeholder="$t('clinical.people.anamnesis.db_hl')"
              :value="value.ull"
              @input="v => updateValue('ull', v)"
              :error="formErrors.has('ull')"
              :error-messages="[$t(formErrors.get('ull'))]"
              :disabled="!editing"
            />
          </div>
        </div>
        -->

        <!-- Tonal Audiometry Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.tonal_audiometry_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="tonal-audiometry-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.tonal_audiometry_obs')"
              :value="value.obs"
              @input="v => updateValue('obs', v)"
              :error="formErrors.has('obs')"
              :error-messages="[$t(formErrors.get('obs'))]"
              :disabled="!editing"
            />
          </div>
        </div>

      </form>
      <div v-else class="ml-2">
        <slot name="noData">
          <span>{{$t('clinical.people.no_data')}}</span>
        </slot>
      </div>
    </div>

  </section>
</template>
<script>
import Errors from '@/utils/StandardErrors'
import Multiselect from 'vue-multiselect'
import * as AnamnesisOptions from './AnamnesisOptions'
import TonalAudiometryMetricsConfigs from './TonalAudiometryMetricsConfigs'

const PTA_AVERAGE_FREQUENCIES = [500, 1000, 2000, 4000]

export default {
  props: {
    value: {
      type: Object,
      required: false
    },
    formErrors: {
      type: Object,
      required: false,
      default: () => {
        return new Errors()
      }
    },
    editing: {
      type: Boolean,
      default: true
    },
    collapsable: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Multiselect
  },
  data: function () {
    return {
      isCollapsed: this.collapsable && this.collapsed,
      formOptions: AnamnesisOptions,
      autoCalcPTALeft: false,
      autoCalcPTARight: false,
      metricsConfigs: TonalAudiometryMetricsConfigs,
      hasUnsavedChanges: false
    }
  },
  computed: {
    cumulativeMetricErrors: function () {
      let errors = this.formErrors.getAll('metric_\\w+_value')
      let uniqueErrors = {}
      errors.forEach(error => {
        if (!uniqueErrors.hasOwnProperty(error)) {
          uniqueErrors[error] = true
        }
      })
      return Object.keys(uniqueErrors)
    }
  },
  methods: {
    getHasUnsavedChanges: function () {
      return this.hasUnsavedChanges
    },
    setHasUnsavedChanges: function (value) {
      this.hasUnsavedChanges = value
    },
    getNormalizedValue: function () {
      let defaultValue = this.getDefaultValue()
      let newValue = { ...defaultValue, ...this.value }
      return newValue
    },
    getDefaultValue: function () {
      return {
        right_ear_avg_areal_hearing_loss: null,
        left_ear_avg_areal_hearing_loss: null,
        right_ear_areal_masking: null,
        left_ear_areal_masking: null,
        right_ear_avg_bone_hearing_loss: null,
        left_ear_avg_bone_hearing_loss: null,
        right_ear_bone_masking: null,
        left_ear_bone_masking: null,
        ull: null,
        metrics: [],
        obs: null
      }
    },
    updateValue: function (field, value) {
      if (this.formErrors.has(field)) {
        this.formErrors.clear(field)
      }
      this.$emit('input', { ...this.value, [field]: value })
      this.$emit('clearErrors', field)

      if (!this.hasUnsavedChanges) {
        this.hasUnsavedChanges = true
      }
    },
    getMetricValue: function (field, ear, frequency) {
      const metric = this.value.metrics.find(metric => metric.field === field && metric.ear === ear && metric.frequency == frequency)
      return metric ? metric.value : null
    },
    updateMetricValue: function (field, ear, frequency, value) {
      let index = this.value.metrics.findIndex(metric => metric.field === field && metric.ear === ear && metric.frequency == frequency)
      value = value.replace(/[^0-9.,+-]/g, '').replace(',', '.').trim()
      if (index !== -1) {
        let updatedValue = { ...this.value }
        if (value.length === 0) {
          updatedValue.metrics.splice(index, 1)
        } else {
          updatedValue.metrics[index].value = value
        }
        this.$emit('input', updatedValue)
        this.$emit('clearErrors', `metric_${field}_${ear}_${frequency}_value`)
      } else {
        this.value.metrics.push(this.createNewMetric(field, ear, frequency, value))

        this.$emit('clearErrors', `metric_${field}_${ear}_${frequency}_value`)
      }

      this.hasUnsavedChanges = true
    },
    calcAveragePTA: function () {
      if (this.value && Array.isArray(this.value.metrics) && this.value.metrics.length) {
        const metrics = this.value.metrics
        const leftPTAValues = metrics.filter(metric => metric.field === 'left_ear_areal_hearing_loss' && PTA_AVERAGE_FREQUENCIES.includes(metric.frequency)).map(metric => parseFloat(metric.value.replace('+', ''))).filter(metric => !isNaN(metric))
        const rightPTAValues = metrics.filter(metric => metric.field === 'right_ear_areal_hearing_loss' && PTA_AVERAGE_FREQUENCIES.includes(metric.frequency)).map(metric => parseFloat(metric.value.replace('+', ''))).filter(metric => !isNaN(metric))

        let newValue = {}
        if (leftPTAValues.length === PTA_AVERAGE_FREQUENCIES.length) {
          let newAverage = leftPTAValues.reduce((acc, currentValue) => { return acc + currentValue }) / leftPTAValues.length

          if (newAverage !== this.value.left_ear_avg_areal_hearing_loss) {
            newValue.left_ear_avg_areal_hearing_loss = newAverage
            this.autoCalcPTALeft = true
          }
        } else if (this.value.left_ear_avg_areal_hearing_loss !== null && this.autoCalcPTALeft) {
          newValue.left_ear_avg_areal_hearing_loss = null
          this.autoCalcPTALeft = false
        }

        if (rightPTAValues.length === PTA_AVERAGE_FREQUENCIES.length) {
          let newAverage = rightPTAValues.reduce((acc, currentValue) => { return acc + currentValue }) / rightPTAValues.length

          if (newAverage !== this.value.right_ear_avg_areal_hearing_loss) {
            newValue.right_ear_avg_areal_hearing_loss = newAverage
            this.autoCalcPTARight = true
          }
        } else if (this.value.right_ear_avg_areal_hearing_loss !== null && this.autoCalcPTARight) {
          newValue.right_ear_avg_areal_hearing_loss = null
          this.autoCalcPTARight = false
        }

        if (Object.keys(newValue).length) {
          this.$emit('input', { ...this.value, ...newValue })

          Object.keys(newValue).forEach(key => {
            this.$emit('clearErrors', key)
          })
        }
      }
    },
    createNewMetric: function (field, ear, frequency, value) {
      return {
        field,
        ear,
        frequency,
        value
      }
    },
    getExpressiveDate: function (value) {
      if (value) {
        if (this.$options.filters.isToday(value)) {
          return this.$t('date_helpers.today')
        } else {
          return this.$options.filters.localTime(value, `DD [${this.$t('date_helpers.day_of_month_separator')}] MMMM [${this.$t('date_helpers.day_of_month_separator')}] YYYY`, '', this.locale)
        }
      }
      return ''
    }
  },
  watch: {
    value: function (value) {
      if ((value === null || value === undefined) && this.editing) {
        this.$emit('input', { ...this.getDefaultValue() })
      }
    },
    'value.metrics': function (metrics) {
      this.calcAveragePTA()
    },
    collapsed: function () {
      this.isCollapsed = this.collapsable && this.collapsed
    },
    editing: {
      handler: function (editing) {
        if (editing) {
          this.$emit('input', this.getNormalizedValue())
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss">
.tonal-audiometry-form {

  table.metrics-table {

    width: 100%;
    table-layout: fixed;

    th, td {
      padding: 0.75rem 0.35rem;
    }

    thead {
      color: inherit !important;
      background-color: #f5f5f5 !important;

      th {
        color: inherit !important;
      }
    }

    .field {
      text-align: left;
      width: 27.5%;
      min-width: 250px;
    }

    .frequency {
      text-align: center;
      min-width: 40px;
      width: 5%;
    }

    .masking {
      text-align: center;
      width: 10%;
    }

    .va-input-wrapper.va-input input{
      text-align: center;
      padding-left: 0px !important;
    }

    .va-input__container__icon-wrapper {
      padding: 0px !important;
    }

  }

}
</style>
